import { cn } from '@/lib/utils';

import { FeedKeyTooltip } from './FeedKeyTooltip';

import type { ComponentType } from 'react';

type FeedGlyph = ComponentType<{ className?: string }>;

type FeedTileProps = {
  glyph: FeedGlyph;
  label: string;
  littlebird?: boolean;
  tint?: string;
  badge?: FeedGlyph;
  // Names the badge, which then shows its name on hover, where it says what the tile does not
  badgeLabel?: string;
  statusLabel?: string;
};

const APP_TINT: Record<string, string> = {
  slack: 'bg-[#f4e9f5] dark:bg-[#33223a]',
  gmail: 'bg-[#fdecea] dark:bg-[#3d2220]',
  outlook: 'bg-[#e6f0fc] dark:bg-[#1c2c42]',
  'google calendar': 'bg-[#e8f0fe] dark:bg-[#1d2b45]',
  'google tasks': 'bg-[#e8f0fe] dark:bg-[#1d2b45]',
  todoist: 'bg-[#fdebe8] dark:bg-[#3d211d]',
  github: 'bg-[#eceef1] dark:bg-[#2a2d33]',
  linear: 'bg-[#ecedfb] dark:bg-[#26284a]',
  notion: 'bg-[#f1f1ef] dark:bg-[#2f2f2f]',
  hubspot: 'bg-[#fff0e8] dark:bg-[#40281c]',
};

export const FeedTile = ({
  glyph: Glyph,
  label,
  littlebird,
  tint,
  badge: Badge,
  badgeLabel,
  statusLabel,
}: FeedTileProps) => (
  <span
    role="img"
    aria-label={statusLabel ? `${label} · ${statusLabel}` : label}
    className="feed-tile relative flex size-[var(--ac-row-tile)]"
  >
    <span
      className={cn(
        'flex size-full items-center justify-center overflow-hidden rounded-[var(--ac-row-tile-radius)]',
        tint ??
          (littlebird
            ? 'bg-[#F4EDD9] dark:bg-[#3A3528]'
            : (APP_TINT[label.toLowerCase()] ?? 'bg-secondary'))
      )}
    >
      <Glyph className="size-[var(--ac-row-glyph)]" />
    </span>
    {Badge && badgeLabel ? (
      // The tile sits in the row's check button, whose own tooltip would open over the badge's; a pointer on the
      // badge does not count as on the button
      <span
        className="contents"
        onPointerMove={(event) => event.preventDefault()}
      >
        <FeedKeyTooltip label={badgeLabel}>
          <span
            role="img"
            aria-label={badgeLabel}
            data-card-icon-badge=""
            className="absolute"
          >
            <Badge />
          </span>
        </FeedKeyTooltip>
      </span>
    ) : Badge ? (
      <span aria-hidden="true" data-card-icon-badge="" className="absolute">
        <Badge />
      </span>
    ) : null}
  </span>
);
